import React, { Component } from "react";
import PropTypes from "prop-types";
import getRsftWelcomeMessageCappedPageCount from "./getRsftWelcomeMessageCappedPageCount";
import setRsftWelcomeMessageCappedPageCount from "./setRsftWelcomeMessageCappedPageCount";
import getUserWouldBeShownOldWelcomeMessage from "./getUserWouldBeShownOldWelcomeMessage";
import getUserRsftWelcomeMessageMetadata from "./getUserRsftWelcomeMessageMetadata";
import getAlternateUrls from "@template/helpers/getAlternateUrls";
import { getContext } from "@src/client/context";
import getFeature from "@src/template/helpers/getFeature";
import {
  trackPageInteraction,
  ANALYTICS_CONTEXT,
  ANALYTICS_INTERACTION_IMPRESSION
} from "@src/helpers/events";
import BasicWelcomeMessage from "./BasicWelcomeMessage";
import RsftWelcomeMessage from "./RsftWelcomeMessage";

const SHOW_RSFT_WELCOME_MESSAGE_PAGE_COUNT_THRESHOLD = 2;

const ANALYTICS_IMPRESSION_DELAY = 2000;

class WelcomeMessageContainer extends Component {
  state = {
    basicWelcomeMessageVisible: false,
    rsftWelcomeMessageVisible: false
  };

  async componentDidUpdate({ isFeaturesLoaded: wasFeaturesLoaded }) {
    const { isFeaturesLoaded } = this.props;

    if (!isFeaturesLoaded || isFeaturesLoaded === wasFeaturesLoaded) {
      return;
    }

    const featureInstance = await getFeature("stc-rsft-welcome-message");
    const bucket = featureInstance?.getVariables().bucket;
    const {
      show: userWouldBeShownRsftWelcomeMessage,
      resolvedDeliveryCountry
    } = await getUserRsftWelcomeMessageMetadata();
    const userWouldBeShownOldWelcomeMessage = getUserWouldBeShownOldWelcomeMessage();

    const experimentShouldBeStarted =
      bucket &&
      (userWouldBeShownOldWelcomeMessage || userWouldBeShownRsftWelcomeMessage);

    if (experimentShouldBeStarted) {
      const isEnabled = featureInstance.isEnabled(true);

      if (isEnabled) {
        if (bucket === "v1" && userWouldBeShownOldWelcomeMessage) {
          this.handleShowBasicWelcomeMessage();
        } else if (bucket === "v2" && userWouldBeShownRsftWelcomeMessage) {
          this.handleShowRSFTWelcomeMessage(resolvedDeliveryCountry);
        }

        return;
      }
    }

    return this.handleShowBasicWelcomeMessage();
  }

  trackImpression = () => {
    window.setTimeout(() => {
      const { country } = this.props;
      const context = getContext();
      const currentBrowseCountry = context.getBrowseCountry();
      const currentCurrency = context.getBrowseCurrency();
      const deliveryCountry = country.countryCode;
      const trackOptions = {
        context: ANALYTICS_CONTEXT.welcomeMessage,
        interaction: ANALYTICS_INTERACTION_IMPRESSION
      };
      const { rsftWelcomeMessageVisible: isRsftMessage } = this.state;

      if (isRsftMessage) {
        trackOptions.properties = {
          deliveryCountry,
          currentBrowseCountry,
          currentCurrency
        };
      }

      trackPageInteraction(trackOptions);
    }, ANALYTICS_IMPRESSION_DELAY);
  };

  handleShowBasicWelcomeMessage = () => {
    const { requestWelcomeMessage, platform } = this.props;

    requestWelcomeMessage(platform, getAlternateUrls());

    this.setState({
      basicWelcomeMessageVisible: true,
      rsftWelcomeMessageVisible: false
    });
  };

  handleShowRSFTWelcomeMessage = resolvedDeliveryCountry => {
    const currentCappedPageCount = getRsftWelcomeMessageCappedPageCount();

    if (
      currentCappedPageCount < SHOW_RSFT_WELCOME_MESSAGE_PAGE_COUNT_THRESHOLD
    ) {
      setRsftWelcomeMessageCappedPageCount(currentCappedPageCount + 1);

      return;
    }

    const { requestWelcomeMessage, platform } = this.props;

    requestWelcomeMessage(
      platform,
      getAlternateUrls(),
      resolvedDeliveryCountry
    );

    this.setState({
      basicWelcomeMessageVisible: false,
      rsftWelcomeMessageVisible: true
    });
  };

  render() {
    const { isFeaturesLoaded, redirectUrl } = this.props;
    const {
      basicWelcomeMessageVisible,
      rsftWelcomeMessageVisible
    } = this.state;

    if (!redirectUrl || !isFeaturesLoaded) {
      return null;
    }

    if (basicWelcomeMessageVisible) {
      return <BasicWelcomeMessage trackImpression={this.trackImpression} />;
    }

    if (rsftWelcomeMessageVisible) {
      return <RsftWelcomeMessage trackImpression={this.trackImpression} />;
    }

    return null;
  }
}

WelcomeMessageContainer.propTypes = {
  isFeaturesLoaded: PropTypes.bool,
  platform: PropTypes.string,
  redirectUrl: PropTypes.object,
  country: PropTypes.object,
  requestWelcomeMessage: PropTypes.func.isRequired
};

export default WelcomeMessageContainer;
